import React, {useMemo, useRef, useState, useEffect} from "react";
import {Link, useHistory, useLocation} from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import {useTranslation} from "react-i18next";
import FeatherIcon from "feather-icons-react";
import {getInitials, Block} from "../../constants";
import {tournamentHeroSubscribers} from "../../utils/tournamentHeroBus";
import {tournamentSearchSubscribers} from "../../utils/tournamentSearchBus";

// baseColor/highlightColor left to the app-wide SkeletonTheme default (see
// index.js) - these all used to hardcode baseColor===highlightColor==='#fff'
// (or, for the tabs-loading placeholder below, a near-white "#fff"/"#f3f3f3"
// pair), which had no shimmer contrast on light theme and read as a plain
// white block sitting on the dark theme's own dark header background.
export const TournamentHeaderSkeleton = () => (
    <div className="tournament-header-wrap">
        <div className="tournament-header-top">
            <Skeleton width={20} height={20} />
            <Skeleton circle={true} width={50} height={50} />
            <div className="tournament-header-titles" style={{ paddingTop: '3px' }}>
                <Skeleton width={160} height={16} style={{ marginBottom: '4px' }} />
                <Skeleton width={80} height={11} />
            </div>
        </div>
        <div className="tournament-header-tabs-wrap">
            <ul className="tournament-header-tabs" style={{ margin: '15px 0 0 0' }}>
                <li><Skeleton width={90} height={20} /></li>
                <li><Skeleton width={70} height={20} /></li>
                <li><Skeleton width={80} height={20} /></li>
                <li><Skeleton width={85} height={20} /></li>
            </ul>
        </div>
    </div>
);

export const TABS = [
    {key: "information", label: "Information"},
    {key: "players", label: "Participants", blockType: Block.PARTICIPANTS},
    {key: "draw-stages", label: "Draw", blockTypes: [Block.GROUPS, Block.DRAWS]},
    {key: "schedule", label: "Schedule", blockType: Block.SCHEDULE},
    // Back as a real tab (own ?tab=rating page, rendering the same
    // RatingTable.js the admin's standalone /admin/ratings/:id page and the
    // rating-badge modal both already use - not TournamentRatingPlayers,
    // which is this tournament's own results list, a different thing) -
    // instead of only reachable through that modal bus (Header.js's
    // openTournamentRating, still used as-is for quick lookups from other
    // tabs - no full tab remount for a badge click). requires: only shown
    // once a rating base is actually linked - RatingTable has no
    // independent "just this tournament" mode to fall back to without one.
    {key: "rating", label: "Rating", blockType: Block.RANKING, requires: (tournament) => !!tournament?.rating},
];

// Maps each real nav tab above to the DOM id its content renders under -
// the same ids TournamentExportModal.js's hidden iframe already
// screenshots one at a time via export=true. Shared by Tournament.js
// (mounts the actual #<id> element) and Header.js (the bottom-nav
// Screenshot button, which lives in a separate React tree and only knows
// the active tab via the ?tab= query param, not Tournament.js's state).
export const SCREENSHOT_TARGETS = {
    information: { targetId: "information-block", fileName: "Information" },
    players: { targetId: "participants-block", fileName: "Participants" },
    "draw-stages": { targetId: "draw-stages-block", fileName: "Draw" },
    schedule: { targetId: "schedule-block", fileName: "Schedule" },
    rating: { targetId: "rating-block", fileName: "Rating" },
};

// shared with TournamentSwipeTabs, so swiping lands on the same next/prev
// tab the header's own nav would show - a block disabled for public view
// shouldn't be swipeable into even though it's still in TABS.
export function getVisibleTabs(tournament, isAuthorized) {
    return TABS.filter(tab => {
        // Checked before the isAuthorized shortcut below - a tab.requires
        // failure (e.g. "rating" with no rating base linked) means there's
        // nothing that tab could actually render, for admin and public
        // viewers alike, not just a permission/visibility toggle.
        if (tab.requires && !tab.requires(tournament)) return false;
        if (isAuthorized) return true;
        const blockTypes = tab.blockTypes || (tab.blockType ? [tab.blockType] : []);
        if (blockTypes.length === 0) return true;
        // Merged "draw-stages" tab covers two blocks (Groups/Draws) - visible
        // as long as at least one of them is active, not only when both are.
        return blockTypes.some(blockType => {
            const block = tournament?.blocks?.find(b => b.block_type === blockType);
            return block ? block.is_active : true;
        });
    });
}

export default function TournamentHeader({user, club, tournament, tournamentId, isAuthorized, rightControls}) {
    const { t } = useTranslation();
    const location = useLocation();
    const history = useHistory();

    // Captured once, on arrival at this tournament - not read live at click
    // time - because in-page tab switches (<Link replace> to ?tab=...) each
    // overwrite history.action to "REPLACE", which used to make a stale
    // reading of it look just as valid as a real PUSH here. That let a
    // single press of this back arrow land on the *previous tab's* URL
    // instead of actually leaving the tournament: goBack() faithfully popped
    // one history entry, which after any tab switch was just this same
    // tournament with a different ?tab=, not wherever the user really came
    // from - hence back "first" undoing in-page content before it would
    // "finally" exit on a second press. Freezing the action at mount
    // instead reflects only how we *arrived* (a real in-app PUSH, e.g. from
    // the tournaments list or a club's tournament card) versus everything
    // else (typing the URL, a shared/external link, or a fresh reload) -
    // where there's no genuine "back" target to pop to, so this goes
    // straight to the tournaments list rather than guessing.
    const arrivalActionRef = useRef(history.action);

    const goUp = (e) => {
        e.preventDefault();
        if (arrivalActionRef.current === "PUSH") {
            history.goBack();
        } else {
            history.push(isAuthorized ? "/admin/tournaments" : "/tournaments");
        }
    };

    const openClubModal = (e) => {
        e.preventDefault();
        const params = new URLSearchParams(location.search);
        params.set("club_id", tournament.club);
        // replace, not push - same reasoning as the tab-switch fix above
        // (see arrivalActionRef's own comment): a push here left an extra
        // history entry goUp's own goBack() would undo first, so pressing
        // the back arrow after opening the club card just closed the card
        // instead of actually leaving the tournament.
        history.replace({ search: params.toString() });
    };

    const activeTab = new URLSearchParams(location.search).get("tab") || "information";

    const visibleTabs = useMemo(() => getVisibleTabs(tournament, isAuthorized), [tournament, isAuthorized]);

    // Tournament.js's own hero cover (mobile-only, only rendered when the
    // tournament has an image - see useTournamentHeroCollapse there) reports
    // its scroll-away progress here so the crest+title below can crossfade
    // in as it approaches full collapse, same idea as ClubProfileModal's
    // .profile-header-compact. Starts at 1 (crest+title fully shown, today's
    // behavior) so a tournament with no hero - the common case - never
    // shows a blank identity row while nothing is publishing progress.
    const [heroProgress, setHeroProgress] = useState(1);
    useEffect(() => {
        const handler = (progress) => setHeroProgress(progress);
        tournamentHeroSubscribers.add(handler);
        return () => tournamentHeroSubscribers.delete(handler);
    }, []);

    // Replaces what used to be a separate search icon duplicated in every
    // tab (Участники/Жеребкування/Розклад/Рейтинг) - Tournament.js owns the
    // actual open/query state (it's the one thing that survives a ?tab=
    // switch, see its own comment) and publishes it here, the same way it
    // already does for tournamentHeroBus above.
    const [searchState, setSearchState] = useState(null);
    useEffect(() => {
        const handler = (state) => setSearchState(state);
        tournamentSearchSubscribers.add(handler);
        return () => tournamentSearchSubscribers.delete(handler);
    }, []);

    // Carries the current stage/place-tab selection along into the target
    // tab's own URL instead of the plain `?tab=X` this used to build, which
    // reset every other param - so leaving Draw on stage 2 (or Grids on a
    // particular place tab) for Players/Schedule/etc. and coming back via
    // this nav lost the selection, always landing back on stage 1. Only
    // `stage`/`gridTab` are carried, not the whole querystring (other
    // params like player_id/club_id open unrelated overlays that shouldn't
    // follow a tab switch) - safe to hand to a tab that doesn't use them
    // (e.g. Schedule inheriting Draw's `stage`) since each tab already
    // validates it against its own stage list before trusting it, falling
    // back to its own default otherwise.
    const buildTabHref = (tabKey) => {
        const params = new URLSearchParams();
        params.set("tab", tabKey);
        if (isAuthorized) params.set("admin", "1");
        const currentParams = new URLSearchParams(location.search);
        const stage = currentParams.get("stage");
        if (stage) params.set("stage", stage);
        const gridTab = currentParams.get("gridTab");
        if (gridTab) params.set("gridTab", gridTab);
        return `/tournaments/${tournamentId}?${params.toString()}`;
    };

    if (!tournament && tournamentId !== "new") {
        return <TournamentHeaderSkeleton />;
    }

    return (
        <div className="tournament-header-wrap tournament-header-loaded">
            <div className="tournament-header-top">
                <a href="#" onClick={goUp} className="tournament-header-back" style={{border: 0, color: 'var(--primary-color)'}}>
                    <FeatherIcon icon="arrow-left" size={20} />
                </a>

                {/* Crossfades in as Tournament.js's own hero cover scrolls
                    away (see heroProgress above) - stays permanently at full
                    opacity whenever there's no hero to begin with, since
                    heroProgress defaults to (and gets reset to) 1 for that
                    case. Wrapped together (not faded separately) so the
                    crest doesn't jump into place a beat ahead of the title
                    next to it. */}
                <div className="tournament-header-identity" style={{ opacity: heroProgress }}>
                    <div className="tournament-header-thumb">
                        {club?.image
                            ? <img
                                src={club.image.startsWith('http') || club.image.startsWith('/') ? club.image : `/media/${club.image}`}
                                className="thumbnail initials-home"
                                alt={club.name}
                                style={{ margin: 0 }}
                            />
                            : club?.name &&
                            <div className="no-thumbnail initials-home" style={{ margin: 0 }}>
                                {getInitials(club.name)}
                            </div>
                        }
                    </div>

                    {/* same title/club styling (and truncation) as the tournament
                        list card's .tournament-card-title/-club, name-first. flex +
                        min-width:0 (not a table cell width hack) is what actually
                        makes the ellipsis work when the name/club is long. */}
                    <div className="tournament-header-titles">
                        <div className="tournament-card-title">
                            {(tournament?.name || t("New tournament"))}
                        </div>
                        <div className="tournament-card-club">
                            {club?.name && tournament?.club ? (
                                <a href="#" onClick={openClubModal} className="tournament-header-club-link" title={t("Club page")}>
                                    <span className="tournament-header-club-name">{club.name}</span>
                                    <FeatherIcon icon="external-link" size={11} />
                                </a>
                            ) : (club?.name || '')}
                        </div>
                    </div>
                </div>

                <div className="tournament-header-right">
                    {searchState && (
                        <a
                            href="#"
                            // Without this, clicking the icon while the search
                            // input is focused steals focus first (native
                            // mousedown behavior) - the input's own onBlur
                            // closes the search right then (query empty), and
                            // this click's onToggle fires a beat later against
                            // that already-closed state, flipping it back open
                            // instead of closing it. Same fix as Участники's
                            // old search toggle used for the identical race.
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => { e.preventDefault(); searchState.onToggle(); }}
                            className="tournament-header-search-toggle"
                            style={{ border: 0, color: 'var(--primary-color)' }}
                            title={t("Search")}
                        >
                            <FeatherIcon icon="search" size={18} />
                        </a>
                    )}
                    {rightControls}
                </div>
            </div>

            <div className="tournament-header-tabs-wrap">
                <ul className="tournament-header-tabs">
                    {!tournament ? (
                        <li><Skeleton width={420} height={20} /></li>
                    ) : (
                        visibleTabs.map((tab) => (
                            <li key={tab.key} className={activeTab === tab.key ? "active" : ""}>
                                <Link replace to={buildTabHref(tab.key)}>
                                    {t(tab.label)}
                                </Link>
                            </li>
                        ))
                    )}
                </ul>
            </div>
        </div>
    );
}
